import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, Button, TextField, Chip, CircularProgress, alpha, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Collapse, IconButton, LinearProgress, MenuItem,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InventoryIcon from "@mui/icons-material/Inventory";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BarChartIcon from "@mui/icons-material/BarChart";
import ViewListIcon from "@mui/icons-material/ViewList";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PageLoader from "../../Components/Loader/PageLoader";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();
const ACCENT = "#00838f";
const ROUTE_COLORS = ["#3b82f6", "#8b5cf6", "#059669", "#ea580c", "#ec4899", "#0891b2", "#d97706", "#6366f1"];

interface DemandProduct {
  product_id: number; product_name: string; unit: string;
  total_quantity: number; current_stock: number; shortage: number;
  routes: { route_id: number | null; route_name: string; quantity: number }[];
}

interface DemandData {
  date_range: { from: string; to: string };
  summary: { total_orders: number; total_products: number; total_quantity: number };
  products: DemandProduct[];
  routes: { id: number; name: string }[];
}

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "this_week", label: "This Week" },
  { key: "next_week", label: "Next Week" },
];

const DemandPlanner: React.FC = () => {
  const [data, setData] = useState<DemandData | null>(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState("today");
  const [routeFilter, setRouteFilter] = useState("all");
  const [viewMode, setViewMode] = useState<"combined" | "route">("combined");
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const getHeaders = () => ({
    "Content-Type": "application/json", Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ preset });
      if (routeFilter !== "all") params.set("route_id", routeFilter);
      const res = await fetch(`${API_URL}/delivery/demand-data?${params}`, { headers: getHeaders() });
      const json = await res.json();
      if (json.status && json.data) setData(json.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [preset, routeFilter]);

  const toggleExpand = (pid: number) => {
    setExpandedRows(prev => { const n = new Set(prev); n.has(pid) ? n.delete(pid) : n.add(pid); return n; });
  };

  // Route-grouped view
  const routeGrouped = useMemo(() => {
    if (!data) return [];
    const m: Record<string, { route_name: string; route_id: number | null; products: { name: string; unit: string; qty: number }[] }> = {};
    data.products.forEach(p => {
      p.routes.forEach(r => {
        const key = String(r.route_id ?? "unassigned");
        if (!m[key]) m[key] = { route_name: r.route_name, route_id: r.route_id, products: [] };
        m[key].products.push({ name: p.product_name, unit: p.unit, qty: r.quantity });
      });
    });
    return Object.values(m).sort((a, b) => a.route_name.localeCompare(b.route_name));
  }, [data]);

  const handleExport = () => {
    if (!data) return;
    const rows = data.products.map(p => ({
      "Product": p.product_name, "Unit": p.unit, "Qty Needed": p.total_quantity,
      "Current Stock": p.current_stock, "Shortage": p.shortage,
      ...Object.fromEntries(p.routes.map(r => [r.route_name, r.quantity])),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0] || {}).map(k => ({ wch: Math.max(k.length, 12) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Demand");
    XLSX.writeFile(wb, `Demand_${preset}_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  const d = data;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, minHeight: "100vh", bgcolor: "#f8fafc" }}>

      {/* ═══ HEADER ═══ */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3,
        p: 2.5, borderRadius: 3, bgcolor: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 42, height: 42, borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #7c3aed, #6d28d9)", boxShadow: "0 4px 14px rgba(124,58,237,0.3)" }}>
            <BarChartIcon sx={{ fontSize: 22, color: "#fff" }} />
          </Box>
          <Box>
            <Typography fontSize={20} fontWeight={800} color="#0f172a" letterSpacing="-0.02em">Demand Planner</Typography>
            <Typography fontSize={12} color="#94a3b8">Product quantity requirements by route</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {/* Presets */}
          {PRESETS.map(p => (
            <Chip key={p.key} label={p.label} size="small" onClick={() => setPreset(p.key)}
              sx={{
                fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2, height: 30,
                ...(preset === p.key
                  ? { bgcolor: "#7c3aed", color: "#fff", boxShadow: "0 2px 8px rgba(124,58,237,0.3)" }
                  : { bgcolor: "#fff", color: "#64748b", border: "1px solid #e2e8f0", "&:hover": { bgcolor: alpha("#7c3aed", 0.04) } }),
              }} />
          ))}
          {/* Route filter */}
          <TextField select size="small" value={routeFilter} onChange={(e) => setRouteFilter(e.target.value)}
            InputProps={{ startAdornment: <AltRouteIcon sx={{ fontSize: 16, color: "#94a3b8", mr: 0.5 }} /> }}
            sx={{ minWidth: 140, "& .MuiOutlinedInput-root": { borderRadius: 2.5, fontSize: 12, bgcolor: "#fff", "& fieldset": { borderColor: "#e2e8f0" } } }}>
            <MenuItem value="all">All Routes</MenuItem>
            {(d?.routes || []).map(r => <MenuItem key={r.id} value={String(r.id)}>{r.name}</MenuItem>)}
          </TextField>
          {/* View toggle */}
          <Box sx={{ display: "flex", bgcolor: "#f1f5f9", borderRadius: 2, p: 0.25 }}>
            <Tooltip title="Combined View">
              <IconButton size="small" onClick={() => setViewMode("combined")}
                sx={{ borderRadius: 1.5, bgcolor: viewMode === "combined" ? "#fff" : "transparent", boxShadow: viewMode === "combined" ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }}>
                <ViewListIcon sx={{ fontSize: 18, color: viewMode === "combined" ? "#0f172a" : "#94a3b8" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Route Split View">
              <IconButton size="small" onClick={() => setViewMode("route")}
                sx={{ borderRadius: 1.5, bgcolor: viewMode === "route" ? "#fff" : "transparent", boxShadow: viewMode === "route" ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }}>
                <AccountTreeIcon sx={{ fontSize: 18, color: viewMode === "route" ? "#0f172a" : "#94a3b8" }} />
              </IconButton>
            </Tooltip>
          </Box>
          {/* Export */}
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />} onClick={handleExport}
            disabled={!d || d.products.length === 0}
            sx={{ borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b" }}>
            Export
          </Button>
        </Box>
      </Box>

      {loading ? (
        <PageLoader />
      ) : !d || d.products.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 10, bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0" }}>
          <ShoppingCartIcon sx={{ fontSize: 48, color: "#e2e8f0", mb: 1 }} />
          <Typography fontSize={16} fontWeight={600} color="#94a3b8">No demand data</Typography>
          <Typography fontSize={12} color="#cbd5e1">No orders found for the selected period</Typography>
        </Box>
      ) : (
        <>
          {/* ═══ KPI CARDS ═══ */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            {[
              { icon: <ShoppingCartIcon />, label: "Orders", value: d.summary.total_orders, color: "#3b82f6", bg: "#eff6ff" },
              { icon: <InventoryIcon />, label: "Products", value: d.summary.total_products, color: "#7c3aed", bg: "#f5f3ff" },
              { icon: <BarChartIcon />, label: "Total Qty", value: d.summary.total_quantity.toFixed(0), color: "#059669", bg: "#ecfdf5" },
              { icon: <WarningAmberIcon />, label: "Shortages", value: d.products.filter(p => p.shortage > 0).length, color: "#dc2626", bg: "#fef2f2" },
            ].map((kpi, i) => (
              <Box key={i} sx={{
                flex: "1 1 0", minWidth: 140, p: 2, borderRadius: 3, bgcolor: "#fff", border: "1px solid #e2e8f0",
                transition: "all 0.2s", "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.04)" },
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: kpi.bg }}>
                    {React.cloneElement(kpi.icon, { sx: { fontSize: 15, color: kpi.color } })}
                  </Box>
                  <Typography fontSize={10} fontWeight={600} color="#94a3b8" textTransform="uppercase">{kpi.label}</Typography>
                </Box>
                <Typography fontSize={22} fontWeight={800} color="#0f172a">{kpi.value}</Typography>
              </Box>
            ))}
          </Box>

          {/* ═══ COMBINED VIEW ═══ */}
          {viewMode === "combined" && (
            <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography fontSize={14} fontWeight={700} color="#0f172a">Product Demand</Typography>
                <Typography fontSize={11} color="#94a3b8">{d.date_range.from === d.date_range.to ? d.date_range.from : `${d.date_range.from} → ${d.date_range.to}`}</Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell sx={{ width: 36 }} />
                      {["Product", "Unit", "Qty Needed", "In Stock", "Shortage", "Status"].map(h => (
                        <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", py: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {d.products.map(p => {
                      const expanded = expandedRows.has(p.product_id);
                      const hasShortage = p.shortage > 0;
                      const stockPct = p.current_stock > 0 ? Math.min(100, (p.current_stock / p.total_quantity) * 100) : 0;
                      return (
                        <React.Fragment key={p.product_id}>
                          <TableRow sx={{ "&:hover": { bgcolor: "#f8fafc" }, bgcolor: hasShortage ? "#fffbfa" : undefined }}>
                            <TableCell sx={{ py: 1, px: 1 }}>
                              <IconButton size="small" onClick={() => toggleExpand(p.product_id)} sx={{ width: 24, height: 24 }}>
                                {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography fontSize={13} fontWeight={600} color="#0f172a">{p.product_name}</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Chip label={p.unit} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: "#f1f5f9", color: "#64748b" }} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography fontSize={14} fontWeight={800} color="#0f172a">{p.total_quantity}</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography fontSize={13} fontWeight={600} color={hasShortage ? "#ea580c" : "#059669"}>{p.current_stock}</Typography>
                                <LinearProgress variant="determinate" value={stockPct}
                                  sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: "#f1f5f9",
                                    "& .MuiLinearProgress-bar": { borderRadius: 2, bgcolor: hasShortage ? "#ea580c" : "#059669" } }} />
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              {hasShortage ? (
                                <Typography fontSize={13} fontWeight={700} color="#dc2626">-{p.shortage}</Typography>
                              ) : (
                                <Typography fontSize={12} color="#059669">Sufficient</Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              {hasShortage ? (
                                <Chip label={`Purchase ${p.shortage} ${p.unit}`} size="small"
                                  sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }} />
                              ) : (
                                <Chip label="OK" size="small" sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: "#ecfdf5", color: "#059669" }} />
                              )}
                            </TableCell>
                          </TableRow>
                          {/* Expanded route breakdown */}
                          <TableRow>
                            <TableCell colSpan={7} sx={{ py: 0, border: "none" }}>
                              <Collapse in={expanded}>
                                <Box sx={{ pl: 6, pr: 2, py: 1.5, mb: 1, bgcolor: "#f8fafc", borderRadius: 2, mx: 1 }}>
                                  <Typography fontSize={10} fontWeight={700} color="#94a3b8" textTransform="uppercase" mb={1}>Route Breakdown</Typography>
                                  {p.routes.map((r, ri) => (
                                    <Box key={ri} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5, borderBottom: ri < p.routes.length - 1 ? "1px solid #e2e8f0" : "none" }}>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: ROUTE_COLORS[ri % ROUTE_COLORS.length] }} />
                                        <Typography fontSize={12} color="#334155" fontWeight={500}>{r.route_name}</Typography>
                                      </Box>
                                      <Typography fontSize={12} fontWeight={700} color="#0f172a">{r.quantity} {p.unit}</Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* ═══ ROUTE SPLIT VIEW ═══ */}
          {viewMode === "route" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {routeGrouped.map((group, gi) => (
                <Box key={gi} sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                  <Box sx={{
                    px: 2.5, py: 1.5, borderBottom: `3px solid ${ROUTE_COLORS[gi % ROUTE_COLORS.length]}`,
                    background: `linear-gradient(135deg, ${alpha(ROUTE_COLORS[gi % ROUTE_COLORS.length], 0.04)} 0%, transparent 100%)`,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: ROUTE_COLORS[gi % ROUTE_COLORS.length] }}>
                        <AltRouteIcon sx={{ fontSize: 15, color: "#fff" }} />
                      </Box>
                      <Typography fontSize={14} fontWeight={700} color="#0f172a">{group.route_name}</Typography>
                    </Box>
                    <Chip label={`${group.products.length} product${group.products.length !== 1 ? "s" : ""}`} size="small"
                      sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: alpha(ROUTE_COLORS[gi % ROUTE_COLORS.length], 0.1), color: ROUTE_COLORS[gi % ROUTE_COLORS.length] }} />
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                          {["Product", "Unit", "Quantity"].map(h => (
                            <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", py: 1.25, textTransform: "uppercase" }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.products.map((p, pi) => (
                          <TableRow key={pi} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                            <TableCell sx={{ fontSize: 12, fontWeight: 500, py: 1.25 }}>{p.name}</TableCell>
                            <TableCell sx={{ py: 1.25 }}>
                              <Chip label={p.unit} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: "#f1f5f9", color: "#64748b" }} />
                            </TableCell>
                            <TableCell sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", py: 1.25 }}>{p.qty}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default DemandPlanner;
